import { cloneDeep, isEmpty, isEqual } from "lodash";
import {
  generate_random_string,
  getTranslatedMenuName,
  getTranslation,
  setFixedPrice
} from "./helpers";
import {
  comboPrice,
  isCombo,
  productPrice,
  productPriceWithoutDeposit,
  toCalculatable,
  totalPriceRaw
} from "./pricing";
import { checkSchedules } from "@/utils/schedules";
import store from "../store";

export const INGREDIENT_GROUP_TYPES = {
  SELECT_ONE: 0,
  SELECT: 1,
  SELECT_MULTIPLE: 2
};

export const INGREDIENT_ORDERER = {
  NAME: 0,
  PRICE: 1,
  PRICE_EXCEPT_ZERO: 2
};

const cartesian = (...a) =>
  a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())));

export function getProductPosition(product) {
  let elem = document.getElementById("item_box_" + product.id);
  if (elem) {
    return elem.getBoundingClientRect();
  }
}

export function getProductExtrasByDeliveryMethod(
  productExtras,
  selectedDeliveryMethod
) {
  let productExtrasByDeliveryMethod = productExtras.filter(productExtra => {
    let extrasByDeliveryMethod = activeExtras(
      productExtra.extras,
      selectedDeliveryMethod
    );
    return (
      extrasByDeliveryMethod &&
      extrasByDeliveryMethod.length > 0 &&
      extrasByDeliveryMethod.some(extra => !extra.hide && !extra.soldout)
    );
  });
  return productExtrasByDeliveryMethod;
}

export function activeExtras(extras, currentDeliveryMethod) {
  return extras.filter(
    extra =>
      extra.delivery_methods.length == 0 ||
      extra.delivery_methods.includes(currentDeliveryMethod)
  );
}

export function sortExtras(product, extras) {
  extras.sort((e1, e2) => {
    let ec1 = product.extras.find(e => e.id === e1.extra_category_id);
    let ec2 = product.extras.find(e => e.id === e2.extra_category_id);
    if (ec1.order_by === ec2.order_by) return ec1.name < ec2.name ? -1 : 1;
    return ec1.order_by - ec2.order_by;
  });
}

function menuDiscount(menu) {
  return (
    menu.components.reduce(
      (acc, comp) =>
        acc +
        productPrice({
          ...comp.selected,
          price: comp.selected.original_price
            ? comp.selected.original_price
            : comp.selected.price
        }),
      0.0
    ) - comboPrice(menu)
  );
}

export function isMenu(basketItem) {
  return (
    typeof basketItem.selectedCombo !== "undefined" && basketItem.selectedCombo
  );
}

export function disassembleBasket(basket) {
  const LIMIT = 5;
  const MENU_DISASSEMBLE_LIMIT = 1;
  let ctr = 0;
  basket = cloneDeep(basket);
  // Section: DISSASSEMBLE AUTOMATICALLY ASSEMBLED MENUS
  console.time("disassemble");
  let disassembled_products = [];
  let disassembled_basket = basket.filter(bi => {
    if (isMenu(bi) && bi.automatically_generated && ctr < LIMIT) {
      ctr += 1;
      if (bi.count <= MENU_DISASSEMBLE_LIMIT) {
        bi.components.forEach(mc => {
          delete mc.selected.fixed_price;
          let price = mc.selected.original_price
            ? mc.selected.original_price
            : mc.selected.price;
          disassembled_products.push({
            ...mc.selected,
            count: bi.count,
            price: price
          });
        });
        return false;
      } else {
        bi.count -= MENU_DISASSEMBLE_LIMIT;
        bi.components.forEach(mc => {
          let product = cloneDeep(mc.selected);
          delete product.fixed_price;
          let price = product.original_price
            ? product.original_price
            : product.price;
          disassembled_products.push({
            ...product,
            count: MENU_DISASSEMBLE_LIMIT,
            price: price
          });
        });
        return true;
      }
    }
    return true;
  });
  disassembled_products.forEach(dp => {
    let existing = findSameBasketItem(dp, disassembled_basket);
    if (existing) {
      existing.count += dp.count;
    } else {
      disassembled_basket.push(dp);
    }
  });
  disassembled_basket = disassembled_basket.map(bi => ({
    ...bi,
    calculated_price: productPriceWithoutDeposit(bi, false, [], 1)
  }));
  console.timeEnd("disassemble");
  // EndSection: DISSASSEMBLE AUTOMATICALLY ASSEMBLED MENUS
  return disassembled_basket;
}

export function validMenusFromBasket(basket, all_menus) {
  // Section: Find ValidMenus that could in theory be assembled from the products in the basket.
  let potential_menu_ids = new Set();
  basket.forEach(bi => {
    if (isEmpty(bi.menus)) return;
    bi.menus.forEach(menu_id => potential_menu_ids.add(menu_id));
  });
  potential_menu_ids = [...potential_menu_ids];
  let potential_menus = potential_menu_ids
    .map(menu_id => all_menus.find(menu => menu.id === menu_id))
    .filter(menu => menu !== undefined);
  let valid_menus = [];
  potential_menus.forEach(menu => {
    let valid_components = [];
    menu.components.forEach(comp => {
      let available_products = basket.filter(
        bi => !isMenu(bi) && comp.available_products.find(ap => ap.id === bi.id)
      );

      valid_components.push(available_products);
    });
    valid_menus.push({ menu, combinations: cartesian(...valid_components) });
  });
  // EndSection: Find ValidMenus that could in theory be assembled from the products in the basket.
  return valid_menus;
}

export function checkBasketAgainstOthers(basket, state, depth) {
  const STATE_DEPTH_KEY = "depth_" + depth;
  const STATE_DEPTH_KEY_B4 = "depth_" + (depth - 1);
  const TOLERANCE =
    Math.max(basket.reduce((acc, bi) => acc + bi.count, 0) - depth * 3, 0) +
    0.5;
  //const TOLERANCE = 5.0;
  let current_basket_price = simplified_price(basket);
  if (
    current_basket_price >
      (state.lowest[STATE_DEPTH_KEY] || Infinity) + TOLERANCE ||
    current_basket_price >
      (state.lowest[STATE_DEPTH_KEY_B4] || Infinity) + TOLERANCE / 2.0
  ) {
    state.discarded_baskets += 1;
    return null;
  }
  if (current_basket_price < (state.lowest[STATE_DEPTH_KEY] || Infinity)) {
    state.lowest[STATE_DEPTH_KEY] = current_basket_price;
  }
  return current_basket_price;
}

export function combineProductsToMenu(
  all_menus,
  orig_basket,
  disassemble = true,
  state = null,
  depth = 0
) {
  if (state === null) {
    state = {
      lowest: {
        depth_0: Infinity
      },
      discarded_baskets: 0,
      discarded_menus: 0,
      calls: 0
    };
  }
  state.calls += 1;
  let basket;
  if (disassemble) {
    basket = disassembleBasket(orig_basket);
    let basket_size = basket.reduce(
      (acc, bi) =>
        acc +
        (bi.automatically_generated === true ||
        bi.automatically_generated === undefined
          ? 1
          : 0),
      0
    );
    console.log("basket size", basket_size, basket);
    if (basket_size > 25) return orig_basket;
    orig_basket = cloneDeep(basket);
  } else {
    basket = cloneDeep(orig_basket);
  }
  let product_basket = basket.filter(bi => !isMenu(bi));

  let valid_menus = validMenusFromBasket(product_basket, all_menus);

  let current_basket_price = checkBasketAgainstOthers(basket, state, depth);
  if (current_basket_price === null) return;

  let valid_baskets = [
    {
      p: current_basket_price,
      l: basket.length,
      c: basket.reduce((acc, bi) => acc + bi.count, 0),
      b: basket
    }
  ]; // add initial basket to valid baskets.

  let biggest_menu_discount = {};
  // Section: Materialize all Valid Menus to a basketItem
  if (disassemble) console.time("Materialize");
  valid_menus.forEach(vm => {
    vm.combinations.forEach(combo_components => {
      let valid = true;
      let temp_basket = cloneDeep(orig_basket);
      let new_menu = cloneDeep(vm.menu);

      new_menu.needAsk = true;
      new_menu.selectedCombo = true;
      new_menu.count = 1;
      new_menu.automatically_generated = true;

      if (!Array.isArray(combo_components)) {
        combo_components = [combo_components];
      }
      let single_price = 0.0;
      combo_components.forEach((comp, idx) => {
        let temp_bi = temp_basket.find(bi => bi.uid === comp.uid);
        if (!temp_bi) {
          valid = false;
          return;
        }
        single_price += temp_bi.calculated_price;
        temp_bi.count -= 1;
        temp_basket = temp_basket.filter(bi => bi.count > 0);
        new_menu.components[idx].selected = { ...comp, count: 1 };
      });
      if (valid) {
        setFixedPrice(new_menu);
        new_menu.calculated_price = productPriceWithoutDeposit(
          new_menu,
          false,
          [],
          1
        );
        let discount = single_price - new_menu.calculated_price;
        if (
          (biggest_menu_discount[new_menu.id] !== undefined &&
            biggest_menu_discount[new_menu.id] >= discount) ||
          discount < 0.0
        ) {
          state.discarded_menus += 1;
          return;
        }
        new_menu.discount = discount;
        new_menu.uid = generate_random_string(16);
        new_menu.components.map(comp => {
          comp.selected.uid = generate_random_string(16);
        });
        biggest_menu_discount[new_menu.id] = discount;
        new_menu.name = getTranslatedMenuName(
          window.global_vue_instance.$store,
          new_menu
        );
        temp_basket = addProductToBasket(new_menu, temp_basket);
        let temp_basket_price = checkBasketAgainstOthers(
          temp_basket,
          state,
          depth
        );
        if (temp_basket_price === null) {
          state.discarded_baskets += 1;
          return;
        }
        const annotated_basket = {
          m_id: new_menu.id,
          p: temp_basket_price,
          l: temp_basket.length,
          c: temp_basket.reduce((acc, bi) => acc + bi.count, 0),
          b: temp_basket
        };
        valid_baskets.push(annotated_basket);
      }
    });
  });
  if (disassemble) console.timeEnd("Materialize");
  // EndSection: Materialize all Valid Menus to a basketItem
  // Early Return, this breaks recursion!!
  if (valid_baskets.length === 1) {
    //No candidate Menus
    return valid_baskets[0].b;
  }
  // select the cheapest basket
  if (disassemble) console.time("sort_1");
  valid_baskets = valid_baskets.sort(basketRate); //lowest priced basket wins
  if (disassemble) console.timeEnd("sort_1");
  //recurse only top menus
  let current_best_basket_price = checkBasketAgainstOthers(
    valid_baskets[0].b,
    state,
    depth
  );
  if (current_best_basket_price === null) return;

  if (disassemble) console.time("recurse");
  valid_menus.forEach(vm => {
    let lowest_annotated_basket = valid_baskets.find(
      vb => vb.m_id === vm.menu.id
    );
    if (!isEmpty(lowest_annotated_basket)) {
      const better_basket = combineProductsToMenu(
        all_menus,
        lowest_annotated_basket.b,
        false,
        state,
        depth + 1
      );
      if (better_basket === undefined) return;
      let better_basket_price = simplified_price(better_basket);
      const annotated_basket = {
        p: better_basket_price,
        l: better_basket.length,
        c: better_basket.reduce((acc, bi) => acc + bi.count, 0),
        b: better_basket
      };
      valid_baskets.push(annotated_basket);
    }
  });
  if (disassemble) {
    console.timeEnd("recurse");
    console.log("ValidBaskets:", valid_baskets);
    console.time("sort_2");
  }
  valid_baskets = valid_baskets.sort(basketRate); //lowest priced basket wins
  if (disassemble) {
    console.timeEnd("sort_2");
    console.log("state", state);
  }
  return valid_baskets[0].b;
}

function simplified_price(basket) {
  return basket.reduce((acc, bi) => acc + bi.count * bi.calculated_price, 0.0);
}

function basketRate(b1, b2) {
  let b1_price = b1.p;
  let b2_price = b2.p;
  if (b1_price === b2_price) {
    if (b1.l === b2.l) {
      return b1.c - b2.c;
    }
    return b1.l - b2.l;
  }
  return b1_price - b2_price;
}

export function getPopulatedIngredientGroups(product) {
  let rv = [];
  Object.keys(product.ingredients).forEach(ingredientGroupName => {
    // ingredientGroupName = (e.g. "0000925d-d050-4938-82e3-6e7d5c057540") or "default"
    let ig_id = ingredientGroupName === "default" ? -1 : ingredientGroupName;
    let ig;
    if (ingredientGroupName === "default") {
      let default_ingredients = product.default_ingredients.filter(
        i => i.group === null
      );
      ig = {
        id: -1,
        name: "default",
        type: INGREDIENT_GROUP_TYPES.SELECT,
        orderer: INGREDIENT_ORDERER.NAME,
        ingredients: default_ingredients,
        order_by: -1,
        price_included_baseprice: "0.00",
        price_included_count: 0
      };
    } else {
      if (product.selectedSize) {
        ig = product.available_ingredient_groups.find(
          aig => aig.id === ig_id && aig.size === product.selectedSize.id
        );
      }
      if (!ig) {
        ig = product.available_ingredient_groups.find(aig => aig.id === ig_id);
      }
    }
    if (!ig) return;
    ig = { ...ig, ingredients: [...ig.ingredients] };
    ig.ingredients = ig.ingredients.filter(ing => !ing.hide);
    ig.ingredients.forEach(ing => {
      let found_ing = product.ingredients[ingredientGroupName].find(
        i => i.id === ing.id
      );
      if (found_ing) {
        ing.count = found_ing.count;
      } else {
        ing.count = 0;
      }
      let found_default = product.default_ingredients.find(
        i =>
          i.id === ing.id &&
          (i.group === ig_id || (i.group === null && ig_id === -1))
      );
      if (found_default) {
        if (toCalculatable(found_default.price) !== 0.0) {
          ing.price = found_default.price;
        }
        ing.default_count = found_default.default_count;
      } else {
        ing.default_count = 0;
      }
      // add translation as "current_display_name"
      ing.current_display_name = getTranslation(
        store,
        "Ingredient",
        "display_name",
        ing.id,
        ing.name
      );
    });
    ig.ingredients.sort((i1, i2) => {
      if (ig.orderer === INGREDIENT_ORDERER.NAME) {
        // sort by translation if current_display_name present
        if (i1.order_by === i2.order_by) {
          if (i1.current_display_name && i2.current_display_name) {
            return i1.current_display_name < i2.current_display_name ? -1 : 1;
          } else {
            return i1.name < i2.name ? -1 : 1;
          }
        }
      } else if (ig.orderer === INGREDIENT_ORDERER.PRICE) {
        if (i1.order_by === i2.order_by) {
          if (i1.price === i2.price) return i1.name < i2.name ? -1 : 1;
          return toCalculatable(i1.price) < toCalculatable(i2.price) ? -1 : 1;
        }
      } else if (ig.orderer === INGREDIENT_ORDERER.PRICE_EXCEPT_ZERO) {
        if (i1.order_by === i2.order_by) {
          if (i1.price === i2.price) return i1.name < i2.name ? -1 : 1;
          if (toCalculatable(i1.price) === 0.0) return 1;
          if (toCalculatable(i2.price) === 0.0) return -1;
          return toCalculatable(i1.price) < toCalculatable(i2.price) ? -1 : 1;
        }
      }
      return i1.order_by - i2.order_by;
    });
    rv.push(ig);
  });
  rv = rv.filter(ig => ig.ingredients.length !== 0);
  rv.sort((ig1, ig2) => {
    if (ig1.order_by === ig2.order_by) return ig1.name < ig2.name ? -1 : 1;
    return ig1.order_by - ig2.order_by;
  });
  return rv;
}

export function addProductToBasket(basketItem, basket) {
  let newBasket;
  let existing = findSameBasketItem(basketItem, basket);
  if (existing) {
    newBasket = basket.map(bi =>
      bi.uid === existing.uid
        ? { ...existing, count: existing.count + basketItem.count }
        : bi
    );
  } else {
    newBasket = [basketItem, ...basket];
  }
  return newBasket;
}

export function updateExactProductInBasket(updated_product, basket) {
  let tmp_basket = basket.map(bi => {
    if (typeof bi.selectedCombo !== "undefined") {
      bi.components = bi.components.map(c => {
        if (c.selected.uid === updated_product.uid) {
          return { ...c, selected: updated_product };
        }
        return c;
      });
      return bi;
    }
    if (bi.uid === updated_product.uid) {
      return updated_product;
    }
    return bi;
  });
  return [...tmp_basket];
}

export function productToBasketItem(pr) {
  const product = createNewObject(pr);
  if (
    product.available_sizes.length !== 0 &&
    typeof product.selectedSize === "undefined"
  ) {
    if (product.default_size) {
      let size = product.available_sizes.find(
        s => s.id === product.default_size
      );
      if (size) {
        product.selectedSize = size;
      }
    }
  }
  if (typeof product.ingredients === "undefined") {
    product.ingredients = { default: [] };
    product.available_ingredient_groups.forEach(group => {
      if (!group.size && group.id in product.ingredients) return; //skip when no size and group id is already set.
      if (
        group.size &&
        product.selectedSize &&
        product.selectedSize.id !== group.size
      )
        return; //ignore different size.
      product.ingredients[group.id] = [];
      group.ingredients.forEach(ing => {
        product.ingredients[group.id].push({
          id: ing.id,
          count: 0
        });
      });
    });
    product.default_ingredients.forEach(item => {
      let group = "default";
      if (item.group !== null) group = item.group;
      let ingredients = product.ingredients[group];
      let ing = ingredients.find(el => el.id === item.id);
      if (ing) {
        ing.count = item.default_count;
      } else {
        ingredients.push({ id: item.id, count: item.default_count });
      }
    });
  }
  if (typeof product.count === "undefined") {
    product.count = 1;
  }
  if (typeof product.uid === "undefined") {
    product.uid = generate_random_string(16);
  }
  return product;
}

export function findSameBasketItem(basketItem, basket) {
  return basket.find(el => {
    if (isCombo(el)) {
      if (!isCombo(basketItem)) return false;
      if (basketItem.components.length !== el.components.length) return false;
      let components_identical = [];
      for (
        let component_index = 0;
        component_index < basketItem.components.length;
        component_index++
      ) {
        let c1 = basketItem.components[component_index].selected;
        let c2 = el.components[component_index].selected;
        let c_fine = [
          isEqual(c1.id, c2.id),
          isEqual(c1.selectedSize, c2.selectedSize),
          isEqual(c1.ingredients, c2.ingredients),
          isEqual(c1.selectedExtras, c2.selectedExtras)
        ].every(Boolean);
        if (!c_fine) return false;
        components_identical.push(c_fine);
      }
      return components_identical.every(Boolean);
    } else if (basketItem.id === el.id) {
      let price_fine = true;
      if (basketItem.isVariant)
        //compare prices if basketItem is a scanned variant
        price_fine = isEqual(basketItem.price, el.price);
      //ID and Size match. Check Ingredients
      let size_fine = isEqual(basketItem.selectedSize, el.selectedSize);
      let ingredients_fine = isEqual(basketItem.ingredients, el.ingredients);
      let extras_fine = isEqual(basketItem.selectedExtras, el.selectedExtras);
      return [price_fine, size_fine, ingredients_fine, extras_fine].every(
        Boolean
      );
    }
    return false;
  });
}

export function findIngredient(product, clazz, ing) {
  let elem = {};
  let clazzes = [clazz];
  let found = clazzes.some(clazz_id => {
    let ingredient_group = product.available_ingredient_groups.find(
      eg => eg.id === clazz_id
    );
    if (ingredient_group) {
      let s = ingredient_group.ingredients.find(e => e.id === ing.id);
      let d = product.default_ingredients.find(e => e.id === ing.id);
      if (s) {
        elem = { ...s, default_count: 0 };
        if (d) {
          elem.default_count = d.default_count;
        }
        return true;
      }
    }
    let s = product.default_ingredients.find(e => e.id === ing.id);
    if (s) {
      elem = { ...s };
      return true;
    }
  });
  if (found) return elem;
  return null;
}

export const createNewObject = obj =>
  obj ? JSON.parse(JSON.stringify(obj)) : obj;

export const getOffer = (offers, name, schedules) => {
  const offer = offers.find(elem => elem.identifier === name);
  if (checkSchedules(schedules, offer)) {
    return offer;
  } else {
    return [];
  }
};
